export const addNodesCytoscape = async (cy: any, elements: any[], anchor: { x: number; y: number }) => {
  if (!elements || !cy) return null;

  const li = cy.scratch('_layout') || {};
  const layoutName: string = li.name || 'dagre';
  const orientation: 'LR' | 'RL' | 'TB' | 'BT' = li.orientation || 'LR';
  const nodeCount = Array.isArray(elements) ? elements.reduce((acc, el) => acc + (el.group === 'nodes' ? 1 : 0), 0) : 0;

  const ORIENT_VEC = {
    LR: { x: 1, y: 0 },
    RL: { x: -1, y: 0 },
    TB: { x: 0, y: 1 },
    BT: { x: 0, y: -1 },
  } as const;

  const PAD = 16;

  function radiusForElement(el: any) {
    const w = Number(el?.data?.width ?? 30);
    const h = Number(el?.data?.height ?? 30);
    return Math.max(12, Math.max(w, h) / 2);
  }

  function radiusForExisting(n: any) {
    const w = typeof n.width === 'function' ? n.width() : (n.data('width') || 30);
    const h = typeof n.height === 'function' ? n.height() : (n.data('height') || 30);
    return Math.max(12, Math.max(w, h) / 2);
  }

  const existing = cy.nodes().toArray();
  const placed: { x: number; y: number; r: number }[] = [];

  function collides(pt: { x: number; y: number }, r: number) {
    for (let i = 0; i < existing.length; i++) {
      const n = existing[i];
      const p = n.position();
      const rr = r + radiusForExisting(n) + PAD;
      const dx = p.x - pt.x;
      const dy = p.y - pt.y;
      if (dx * dx + dy * dy < rr * rr) return true;
    }
    for (let j = 0; j < placed.length; j++) {
      const q = placed[j];
      const rr = r + q.r + PAD;
      const dx = q.x - pt.x;
      const dy = q.y - pt.y;
      if (dx * dx + dy * dy < rr * rr) return true;
    }
    return false;
  }

  function findFreeAround(base: { x: number; y: number }, r: number, step: number) {
    if (!collides(base, r)) return base;

    const MAX_RING = 20;
    for (let ring = 1; ring <= MAX_RING; ring++) {
      const span = ring * step;
      for (let x = -span; x <= span; x += step) {
        const pTop = { x: base.x + x, y: base.y - span };
        if (!collides(pTop, r)) return pTop;
        const pBot = { x: base.x + x, y: base.y + span };
        if (!collides(pBot, r)) return pBot;
      }
      for (let y = -span + step; y <= span - step; y += step) {
        const pL = { x: base.x - span, y: base.y + y };
        if (!collides(pL, r)) return pL;
        const pR = { x: base.x + span, y: base.y + y };
        if (!collides(pR, r)) return pR;
      }
    }
    return base;
  }

  function placeRanked(idx: number, count: number, anchorPos: { x: number; y: number }, orient: 'LR' | 'RL' | 'TB' | 'BT', r: number) {
    const dir = ORIENT_VEC[orient] || ORIENT_VEC.LR;
    const perp = { x: -dir.y, y: dir.x };
    const cols = Math.ceil(Math.sqrt(count));
    const row = Math.floor(idx / cols);
    const col = idx % cols;
    const alongStart = 200;
    const alongGap = Math.max(2 * r + 40, 120);
    const perpGap = Math.max(2 * r + 20, 90);
    const colCenterShift = (cols - 1) / 2;
    const base = { x: anchorPos.x + dir.x * (alongStart + row * alongGap), y: anchorPos.y + dir.y * (alongStart + row * alongGap), };
    const prelim = { x: base.x + perp.x * (col - colCenterShift) * perpGap, y: base.y + perp.y * (col - colCenterShift) * perpGap, };
    const step = Math.min(perpGap, alongGap);
    return findFreeAround(prelim, r, step);
  }

  function placeGrid(idx: number, count: number, anchorPos: { x: number; y: number }, r: number) {
    const cols = Math.ceil(Math.sqrt(count));
    const row = Math.floor(idx / cols);
    const col = idx % cols;
    const gap = Math.max(2 * r + 24, 100);
    const startX = anchorPos.x - ((cols - 1) * gap) / 2;
    const startY = anchorPos.y - ((Math.ceil(count / cols) - 1) * gap) / 2;
    const prelim = { x: startX + col * gap, y: startY + row * gap };
    return findFreeAround(prelim, r, gap);
  }

  function computeDest(
    idx: number,
    cnt: number,
    anchorPos: { x: number; y: number },
    r: number
  ) {
    switch (layoutName) {
      case 'dagre':
      case 'klay':
        return placeRanked(idx, cnt, anchorPos, orientation, r);
      default:
        return placeGrid(idx, cnt, anchorPos, r);
    }
  }

  let runningIdx = -1;
  elements.forEach((element) => {
    if (element.group !== 'nodes') return;

    runningIdx += 1;

    const r = radiusForElement(element);
    const dest = computeDest(runningIdx, nodeCount, anchor, r);

    placed.push({ ...dest, r });

    element.data = element.data || {};
    element.data.__dest = dest;
    element.position = { x: anchor.x, y: anchor.y };
    element.data.__startWidth = element.data.width;
    element.data.__startHeight = element.data.height;
  });

  const added = cy.add(elements);
  const addedNodes = added.nodes();

  addedNodes.forEach((n: any) => {
    n.style({
      opacity: 0,
      width: Math.max(8, (n.data('__startWidth') || 30) * 0.6),
      height: Math.max(8, (n.data('__startHeight') || 30) * 0.6),
      'border-width': Math.max(0.5, (n.data('borderWidth') || 1) * 0.6),
    });
  });

  addedNodes.forEach((n: any) => {
    const dest = n.data('__dest') || n.position();
    const targetW = n.data('__startWidth') || n.width();
    const targetH = n.data('__startHeight') || n.height();
    const targetBorder = n.data('borderWidth') || 1;

    n.animation({
      position: dest,
      style: {
        opacity: 1,
        width: targetW,
        height: targetH,
        'border-width': targetBorder,
      },
      duration: 320,
      easing: 'ease-out',
    })
      .play()
      .promise()
      .then(() => {
        n.removeData('__dest');
        n.removeData('__startWidth');
        n.removeData('__startHeight');
      });
  });
};


// const addNodesCytoscape = async (nodes, position) => {
//   if (!nodes) return null;

//   const sizeNodesArray = Object.keys(nodes).length / 2;

//   nodes.forEach((element, index) => {
//     if (element.group === 'nodes') {
//       let destX: number;
//       let destY: number;

//       if (sizeNodesArray > 20) {
//         destX = position.x + index * 100;
//         destY = position.y + index * 40 + 200;
//       } else {
//         destX = position.x + 200 * Math.cos((2 * Math.PI * index) / sizeNodesArray);
//         destY = position.y + 200 * Math.sin((2 * Math.PI * index) / sizeNodesArray);
//       }

//       element.data = element.data || {};
//       element.data.__dest = { x: destX, y: destY };
//       element.position = { x: position.x, y: position.y };
//       element.data.__startWidth = element.data.width;
//       element.data.__startHeight = element.data.height;
//     }
//   });

//   const added = cy.add(nodes);
//   const addedNodes = added.nodes();
//   addedNodes.forEach((n) => {
//     n.style({
//       opacity: 0,
//       width: Math.max(8, (n.data('__startWidth') || 30) * 0.6),
//       height: Math.max(8, (n.data('__startHeight') || 30) * 0.6),
//       'border-width': Math.max(0.5, (n.data('borderWidth') || 1) * 0.6),
//     });
//   });

//   addedNodes.forEach((n) => {
//     const dest = n.data('__dest') || n.position();
//     const targetW = n.data('__startWidth') || n.width();
//     const targetH = n.data('__startHeight') || n.height();
//     const targetBorder = n.data('borderWidth') || 1;

//     n.animation({
//       position: dest,
//       style: {
//         opacity: 1,
//         width: targetW,
//         height: targetH,
//         'border-width': targetBorder,
//       },
//       duration: 320,
//       easing: 'ease-out',
//     })
//       .play()
//       .promise()
//       .then(() => {
//         n.removeData('__dest');
//         n.removeData('__startWidth');
//         n.removeData('__startHeight');
//       });
//   });
// };

